import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Home from './pages/Home';
import Explore from './pages/Explore';
import Submit from './pages/Submit';
import Workspace from './pages/Workspace';
//import Notifications from './pages/Notifications';
import APIDocumentation from './pages/APIDocumentation';
import Profile from './pages/Profile';
import SubmissionDetail from './pages/SubmissionDetail';
import About from './pages/About';
import MobileNavigation from './components/MobileNavigation';
import SignInPage from './components/SignIn';
import SignUpPage from './components/SignUp';
import ProtectedRoute from './components/ProtectedRoute';
import { useUser,useAuth} from '@clerk/clerk-react'; 

function App() {
  const { user, isLoaded } = useUser();
  const [darkMode, setDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [profileInitialized, setProfileInitialized] = useState(false); // 防止重复初始化
  const { getToken } = useAuth();
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);  useEffect(() => {
        const initializeProfile = async () => {
            // 防止重复初始化
            if (profileInitialized) {
                console.log("Profile already initialized, skipping...");
                return;
            }
            
            if (!isLoaded || !user) {
                return;
            }
            const token = await getToken(); 
            if (!token) {
                console.warn("Clerk token not available, skipping profile initialization.");
                return;
            }
              console.log("Clerk User loaded. Attempting to initialize profile...");
            
            // ✅ 第一步：先检查用户是否已存在
            const checkUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:35001'}/api/profile/${user.id}`;
            
            try {
                const checkResponse = await fetch(checkUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                if (checkResponse.ok) {
                    // 用户已存在，跳过初始化
                    console.log("✅ Profile already exists in database, skipping initialization.");
                    setProfileInitialized(true);
                    return;
                } else if (checkResponse.status === 404) {
                    // 用户不存在，继续创建
                    console.log("Profile not found, proceeding with initialization...");
                } else {
                    console.warn("Failed to check profile existence. Status:", checkResponse.status);
                    return;
                }
            } catch (error) {
                console.error("Error checking profile existence:", error);
                return;
            }
            
            // ✅ 第二步：只有在确认用户不存在时才创建
            const primaryEmail = user.primaryEmailAddress?.emailAddress || user.emailAddresses[0]?.emailAddress;
            const fullName = user.fullName || user.username;
            const profilePayload = {
              user_id: user.id, 
              
              email: primaryEmail || null, 
              name: user.fullName || user.username || "New User",
            
              title: null, 
              affiliation: null,
              location: null,
              bio: null,
              
              website: null,
              github: null,
              twitter: null,
              linkedin: null,
              avatar_url: user.imageUrl || null,
              
              // New required academic fields
              institutional_email: primaryEmail || null, // Use primary email as institutional email initially
              orcid: null,
              google_scholar_url: null,
              openreview_id: null,
              academic_status: "other", // Required field - default to "other"
              default_category: null
            };

            const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:35001'}/api/profile/create`;
            
            try {
                
                const response = await fetch(apiUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json',
                               'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(profilePayload),
                });
                
                
                if (response.ok) {
                    console.log("✅ Profile successfully initialized!");
                    // 标记为已初始化
                    setProfileInitialized(true);
                } else {
                    
                    console.error("Failed to initialize profile. Status:", response.status);
                    // ✅ 新增：读取并打印 422 错误详情
                    const errorJson = await response.json(); 
                    console.error("❌ PROFILE INITIALIZATION FAILED:", response.status);
                    console.error("   Detailed Validation Errors:", errorJson.detail); // FastAPI 422 错误通常在 .detail 中
                    
                    // 打印每个具体的错误
                    if (errorJson.detail && Array.isArray(errorJson.detail)) {
                        errorJson.detail.forEach((err, index) => {
                            console.error(`   Error ${index + 1}:`, {
                                field: err.loc ? err.loc.join(' -> ') : 'unknown',
                                message: err.msg,
                                type: err.type,
                                input: err.input
                            });
                        });
                    }
                    
                    console.error("   Sent Payload:", profilePayload); // 打印发送的 Payload
                }

            } catch (error) {
                console.error("API error during profile initialization:", error);
                
            }
        };

        initializeProfile();
    }, [user, isLoaded, profileInitialized, getToken]); // 添加所有依赖项

  return (
    <Router>
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300`}>
        <Header darkMode={darkMode} setDarkMode={setDarkMode} />
        
        <main className={`${isMobile ? 'pb-16' : ''}`}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route path="/submit" element={
              <ProtectedRoute>
                <Submit />
              </ProtectedRoute>
            } />
            <Route path="/workspace/*" element={
              <ProtectedRoute>
                <Workspace />
              </ProtectedRoute>
            } />
            {/* <Route path="/notifications" element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            } /> */}
            <Route path="/api" element={<APIDocumentation />} />
            <Route path="/profile/:id" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/abs/:id" element={<SubmissionDetail />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </main>

        {isMobile && <MobileNavigation />}
      </div>
    </Router>
  );
}

export default App;
